import { AbstractControl, Validators as DefaultValidators, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Assert } from '@sb/library/data-models';
import { ErrorKey, ValidatorKeys } from './form-validation.definitions';

// Much of this code is adapted (or taken) directly from Angular source

export const ValidationLimits = {
    Numbers: {
        Min: 0,
        Max: 1000000000,
        // JavaScript switches to scientific notation at 1e21, which breaks number fields.
        // Clamp to this limit in the UI to prevent that, independently of the validator Max.
        DisplayMax: 1e20,
        Duration: {
            Minutes: 1440, // 24 hours
            Hours: 8760 // 1 year
        }
    },
    Text: {
        MaxLength: 255
    },
    TextArea: {
        MaxLength: 1000000
    },
    Url : {
        MaxLength: 2048
    }
} as const;

export class Validators extends DefaultValidators {
    private static Tag(fn: ValidatorFn, key: ErrorKey): ValidatorFn {
        fn.validatorKey = key;
        return fn;
    }

    static override required(control: AbstractControl): ValidationErrors | null {
        return Validators.Tag(super.required, ValidatorKeys.required)(control);
    }

    static override requiredTrue(control: AbstractControl): ValidationErrors | null {
        return Validators.Tag(super.requiredTrue, ValidatorKeys.required)(control);
    }

    static override email(control: AbstractControl): ValidationErrors | null {
        return Validators.Tag(super.email, ValidatorKeys.email)(control);
    }

    static override min(min: number): ValidatorFn {
        return Validators.Tag(super.min(min), ValidatorKeys.min);
    }

    static override max(max: number): ValidatorFn {
        return Validators.Tag(super.max(max), ValidatorKeys.max);
    }

    static minExclusive(min: number): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value) || isEmptyInputValue(min)) {
                return null;  // don't validate empty values to allow optional controls
            }

            const value = parseFloat(control.value);
            // Controls with NaN values after parsing should be treated as not having a
            // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
            return !isNaN(value) && value <= min ? {'minExclusive': {'min': min, 'actual': control.value}} : null;
        }, ValidatorKeys.min);
    }

    static dependentMin(otherControl: FormControl<number>): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value) || isEmptyInputValue(otherControl)) {
                return null;  // don't validate empty values to allow optional controls
            }

            const value = parseFloat(control.value);
            // Controls with NaN values after parsing should be treated as not having a
            // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
            return !isNaN(value) && value < otherControl.value ? {'min': {'min': otherControl.value, 'actual': control.value}} : null;
        }, ValidatorKeys.min);
    }

    static maxExclusive(max: number): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value) || isEmptyInputValue(max)) {
                return null;  // don't validate empty values to allow optional controls
            }

            const value = parseFloat(control.value);
            // Controls with NaN values after parsing should be treated as not having a
            // maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
            return !isNaN(value) && value >= max ? {'maxExclusive': {'max': max, 'actual': control.value}} : null;
        }, ValidatorKeys.max);
    }

    static dependentMax(otherControl: FormControl<number>): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value) || isEmptyInputValue(otherControl)) {
                return null;  // don't validate empty values to allow optional controls
            }

            const value = parseFloat(control.value);
            // Controls with NaN values after parsing should be treated as not having a
            // maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
            return !isNaN(value) && value > otherControl.value ? {'max': {'max': otherControl.value, 'actual': control.value}} : null;
        }, ValidatorKeys.max);
    }

    static duplicate(blackList: string[], caseSensitive: boolean = true): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value) || isEmptyInputValue(blackList)) {
                return null;  // don't validate empty values to allow optional controls
            }

            let checkValue = (control.value as string).trim();
            if (!caseSensitive) {
                blackList = blackList.map(b => b.toLowerCase());
                checkValue = checkValue.toLowerCase();
            }

            return blackList.includes(checkValue) ? {'duplicate': control.value} : null;
        }, ValidatorKeys.duplicate);
    }

    static match(text: string): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null =>
            (text?.trim() !== (control.value as string)?.trim()) ? {'match': control.value} : null, ValidatorKeys.match);
    }

    static url(): ValidatorFn {
        const URLRegex = /^\s*(http|https):\/\//i;
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value)) {
                return null;  // don't validate empty values to allow optional controls
            }
            return URLRegex.test(control.value) ? null : {'url': control.value};
        }, ValidatorKeys.url);
    }

    static regex(regex: RegExp): ValidatorFn {
        const RegexExpression = regex;
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value)) {
                return null;  // don't validate empty values to allow optional controls
            }

            return RegexExpression.test(control.value) ? null : {'regex': control.value};
        }, ValidatorKeys.regex);
    }

    static password(minLength: number): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value)) {
                return null;  // don't validate empty values to allow optional controls
            }

            let password: string = control.value.trim();
            let passwordLength = password.length;

            let  complexityScore: number = 0;

            if (password.match(/[A-Z]/)) {
                complexityScore++;
            }

            if (password.match(/[a-z]/)) {
                complexityScore++;
            }

            if (password.match(/[0-9]/)) {
                complexityScore++;
            }

            if (password.match(/[^0-9a-zA-Z]/)) {
                complexityScore++;
            }

            const isValid = passwordLength >= minLength && complexityScore >= 3;

            return !isValid ? {'password': {'min': minLength, 'actual': passwordLength, 'score': complexityScore}} : null;
        }, ValidatorKeys.password);
    }

    static override minLength(minLength: number, filter?: string): ValidatorFn {
        let filterRegexp: RegExp | null = null;
        if (filter) {
            filterRegexp = new RegExp(`(${filter})|.`, 'g');
        }
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            let value = control.value;
            if (value && filter) {
                value = value?.replace(filterRegexp, '$1');
            }

            return hasValidLength(value) && value.length < minLength
                 ? {minlength: {requiredLength: minLength, actualLength: value.length}}
                 : null;
        }, ValidatorKeys.minlength);
    }

    static override maxLength(maxLength: number, filter?: string): ValidatorFn {
        let filterRegexp: RegExp | null = null;
        if (filter) {
            filterRegexp = new RegExp(`(${filter})|.`, 'g');
        }
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            let value = control.value;
            if (value && filter) {
                value = value?.replace(filterRegexp, '$1');
            }

            return hasValidLength(value) && value.length > maxLength
                 ? {maxlength: {requiredLength: maxLength, actualLength: value.length}}
                 : null;
        }, ValidatorKeys.maxlength);
    }

    static exactLength(maxLength: number, filter?: string): ValidatorFn {
        let filterRegexp: RegExp | null = null;
        if (filter) {
            filterRegexp = new RegExp(`(${filter})|.`, 'g');
        }
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            let value = control.value;
            if (value && filter) {
                value = value?.replace(filterRegexp, '$1');
            }

            return hasValidLength(value) && value.length !== maxLength
                 ? {exactlength: {requiredLength: maxLength, actualLength: value.length}}
                 : null;
        }, ValidatorKeys.exactlength);
    }

    static exactDigit(maxLength: number): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            let value = control.value;
            if (value) {
                value = value.toString();
            }
            return hasValidLength(value) && value.length !== maxLength
                ? {exactDigit: {requiredLength: maxLength, actualLength: value.length}}
                : null;
        }, ValidatorKeys.exactDigit);
    }

    // taken from max validator but uses control.value.size, i.e., file.size
    static maxFileSize(max: number): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value) || isEmptyInputValue(max)) {
                return null;  // don't validate empty values to allow optional controls
            }

            return (control.value).some((file: File) => (!isNaN(file.size) && file.size > max))
                ? {'max': {'max': max }}
                : null;
        }, ValidatorKeys.maxFileSize);
    }

    static subform(control: AbstractControl): ValidationErrors | null {
        Assert(control instanceof FormControl);
        if (!control.connectedForm) {
            throw Error('Subform validator can only be used on a control with a connected subform (i.e., a control that inherits from FormFieldSubformBaseComponent)');
        }
        return !control.connectedForm!.invalid ? null : { subform: true };
    }

    static maxJoinedLength(maxLength: number): ValidatorFn {
        return Validators.Tag((control: AbstractControl): ValidationErrors | null => {
            if (!Array.isArray(control.value)) {
                return null;
            }
            const actualJoinedLength = control.value.join(";").length;
            return actualJoinedLength > maxLength
                ? {maxlength: {requiredLength: maxLength, actualLength: actualJoinedLength }}
                : null;
        }, ValidatorKeys.maxlength);
    }
}

export function isEmptyInputValue(value: any): boolean {
    /**
   * Check if the object is a string or array before evaluating the length attribute.
   * This avoids falsely rejecting objects that contain a custom length attribute.
   * For example, the object {id: 1, length: 0, width: 0} should not be returned as empty.
   */
    return value == null ||
        ((typeof value === 'string' || Array.isArray(value)) && value.length === 0);
}

export function hasValidLength(value: any): boolean {
    // non-strict comparison is intentional, to check for both `null` and `undefined` values
    return value != null && typeof value.length === 'number';
}
